import { useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import type { ArrayEffect } from '../types/arrayEffect';
import type { FormRule } from '../types/block';
import type { Compute } from '../types/compute';
import { evaluateCompute } from '../evaluators/computeEvaluator';
import { evaluateCondition } from '../evaluators/conditionEvaluator';
import type { ConditionContext } from '../evaluators/conditionEvaluator';
import { ActionRegistry } from '@packages/table-builder/registry';
import {
  markProgrammaticWrites,
  type ProgrammaticWriteGuardRef,
} from './programmaticWriteGuard';
import {
  buildArrayRevalidateTriggerPaths,
  collectWrittenRowFieldNames,
  deriveArrayRevalidateTargets,
} from './revalidationDependencyMap';
import { prefixCompute } from './pathRewriter';

interface UseArrayEffectProcessorParams {
  form: UseFormReturn<Record<string, unknown>>;
  arrayEffects: ArrayEffect[] | undefined;
  arrayName: string;
  meta: Record<string, unknown>;
  getOptionProp: (field: string, prop: string) => unknown;
  hydratingRef?: React.RefObject<boolean>;
  hydrationEpochRef?: React.RefObject<number>;
  programmaticWriteGuard?: ProgrammaticWriteGuardRef;
  itemRules?: FormRule[];
  revalidateOnPerRowSync?: boolean;
  revalidateTargetsOnPerRowSync?: string[];
}

export function useArrayEffectProcessor({
  form,
  arrayEffects,
  arrayName,
  meta,
  getOptionProp,
  hydratingRef,
  hydrationEpochRef,
  programmaticWriteGuard,
  itemRules,
  revalidateOnPerRowSync,
  revalidateTargetsOnPerRowSync,
}: UseArrayEffectProcessorParams): void {
  const effects = arrayEffects ?? [];

  const watchFields = useMemo(() => {
    const fields = new Set<string>();
    for (const effect of effects) {
      const watches = Array.isArray(effect.watch) ? effect.watch : [effect.watch];
      for (const w of watches) fields.add(w);
    }
    return Array.from(fields);
  }, [effects]);

  const watchedValues = useWatch({
    control: form.control,
    name: watchFields.length > 0 ? watchFields : ['__noop__'],
    disabled: watchFields.length === 0,
  });

  const prevSyncValuesRef = useRef<Record<string, unknown>>({});
  const prevAsyncValuesRef = useRef<Record<string, unknown>>({});
  const prevFormSnapshotRef = useRef<Record<string, unknown> | null>(null);
  const isInitialSyncRef = useRef(true);
  const isInitialAsyncRef = useRef(true);
  const prevEpochRef = useRef(hydrationEpochRef?.current ?? 0);
  const lastComputedRef = useRef<Map<string, Map<string, unknown>>>(new Map());
  const userEditedRef = useRef<Set<string>>(new Set());
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    if (effects.length === 0) return;
    const perRowEffects = effects.filter((e) => e.perRow && (e.preserveUserEdits ?? true));
    if (perRowEffects.length === 0) return;

    const targetFields = new Set<string>();
    for (const effect of perRowEffects) {
      for (const field of Object.keys(effect.perRow!)) {
        targetFields.add(field);
      }
    }

    const sub = form.watch((_, { name, type }) => {
      if (!name || type !== 'change') return;
      for (const field of targetFields) {
        if (name.startsWith(`${arrayName}.`) && name.endsWith(`.${field}`)) {
          userEditedRef.current.add(name);
        }
      }
    });
    return () => sub.unsubscribe();
  }, [form, effects, arrayName]);

  const executeActionId = useCallback(
    async (effect: ArrayEffect, signal: AbortSignal) => {
      const currentValues = form.getValues() as Record<string, unknown>;
      const resolvedParams: Record<string, unknown> = {};
      const hasWildcard = effect.params != null && Object.values(effect.params).includes('*');
      if (effect.params) {
        for (const [key, path] of Object.entries(effect.params)) {
          resolvedParams[key] = path === '*' ? currentValues : getNestedValue(currentValues, path);
        }
      }
      if (hasWildcard && prevFormSnapshotRef.current != null) {
        resolvedParams.previousForm = prevFormSnapshotRef.current;
      }
      prevFormSnapshotRef.current = { ...currentValues };

      const result = await ActionRegistry.executeData(effect.actionId!, resolvedParams as Record<string, string>);

      if (signal.aborted) return;

      const rawRows: unknown = result?.rows ?? (Array.isArray(result) ? result : null);

      if (rawRows != null && Array.isArray(rawRows)) {
        const rows = rawRows;
        const currentArray = form.getValues(arrayName) as unknown[] | undefined;

        if (currentArray && currentArray.length === rows.length) {
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i] as Record<string, unknown>;
            const cur = currentArray[i] as Record<string, unknown> | undefined;
            for (const [key, val] of Object.entries(row)) {
              if (cur?.[key] !== val) {
                form.setValue(`${arrayName}.${i}.${key}` as never, val as never, {
                  shouldDirty: false,
                  shouldValidate: false,
                });
              }
            }
          }
        } else {
          form.setValue(arrayName as never, rows as never, { shouldDirty: false, shouldValidate: false });
        }
      }
    },
    [form, arrayName],
  );

  useLayoutEffect(() => {
    if (effects.length === 0 || watchFields.length === 0) return;

    const watchMap: Record<string, unknown> = {};
    watchFields.forEach((field, idx) => {
      watchMap[field] = Array.isArray(watchedValues) ? watchedValues[idx] : watchedValues;
    });

    if (isInitialSyncRef.current) {
      isInitialSyncRef.current = false;
      prevSyncValuesRef.current = { ...watchMap };
      // Capture initial form snapshot so the first real change correctly
      // computes previousValues for `changed` conditions.
      prevFormSnapshotRef.current = { ...(form.getValues() as Record<string, unknown>) };
      return;
    }

    if (hydratingRef?.current) {
      prevSyncValuesRef.current = { ...watchMap };
      prevFormSnapshotRef.current = { ...(form.getValues() as Record<string, unknown>) };
      return;
    }

    if (hydrationEpochRef && hydrationEpochRef.current !== prevEpochRef.current) {
      prevSyncValuesRef.current = { ...watchMap };
      prevEpochRef.current = hydrationEpochRef.current;
      return;
    }

    const perRowEffects = effects.filter((e) => e.perRow);
    const allFieldsToWrite: string[] = [];
    for (const effect of perRowEffects) {
      const watches = Array.isArray(effect.watch) ? effect.watch : [effect.watch];
      const hasChanged = watches.some((w) => watchMap[w] !== prevSyncValuesRef.current[w]);
      if (!hasChanged) continue;

      if (effect.condition) {
        const currentValues = form.getValues() as Record<string, unknown>;
        const condCtx: ConditionContext = {
          values: currentValues,
          previousValues: prevFormSnapshotRef.current ?? undefined,
          meta,
          getOptionProp,
        };
        if (!evaluateCondition(effect.condition, condCtx)) continue;
      }

      const initialValues = form.getValues() as Record<string, unknown>;
      const rows = getNestedValue(initialValues, arrayName) as unknown[] | undefined;
      if (!rows || rows.length === 0) continue;

      const preserveEdits = effect.preserveUserEdits ?? true;
      const fieldsToWrite: string[] = [];

      for (let i = 0; i < rows.length; i++) {
        for (const [field, compute] of Object.entries(effect.perRow!)) {
          const fullPath = `${arrayName}.${i}.${field}`;

          if (preserveEdits && userEditedRef.current.has(fullPath)) continue;

          const liveValues = form.getValues() as Record<string, unknown>;
          const rowPrefix = `${arrayName}.${i}`;
          const withRowIndex = replaceRowIndex(compute, rowPrefix);
          // Bare field names in array-level perRow reference root form fields.
          // Only run pathRewriter when ^ parent refs are present.
          const resolvedCompute = JSON.stringify(withRowIndex).includes('^')
            ? prefixCompute(withRowIndex, rowPrefix)
            : withRowIndex;
          const computed = evaluateCompute(resolvedCompute, {
            fieldValue: null,
            values: liveValues,
            meta,
            getOptionProp,
          });

          // Skip writing null/undefined — the source field isn't set yet (e.g.
          // offeringPeriodEndDate not filled when only start date changed).
          if (computed == null) continue;

          fieldsToWrite.push(fullPath);

          let effectMap = lastComputedRef.current.get(effect.id ?? '');
          if (!effectMap) {
            effectMap = new Map();
            lastComputedRef.current.set(effect.id ?? '', effectMap);
          }
          effectMap.set(fullPath, computed);

          form.setValue(fullPath, computed, { shouldDirty: false, shouldValidate: false });
        }
      }

      if (fieldsToWrite.length > 0) {
        markProgrammaticWrites(programmaticWriteGuard, fieldsToWrite);
        allFieldsToWrite.push(...fieldsToWrite);
      }
    }

    if (allFieldsToWrite.length > 0) {
      scheduleArrayRevalidationAfterPerRowSync({
        form,
        arrayName,
        fieldsToWrite: allFieldsToWrite,
        itemRules,
        revalidateOnPerRowSync,
        revalidateTargetsOnPerRowSync,
      });
    }

    prevFormSnapshotRef.current = { ...(form.getValues() as Record<string, unknown>) };
    prevSyncValuesRef.current = { ...watchMap };
  }, [watchedValues, watchFields, effects, form, arrayName, meta, getOptionProp, hydratingRef, hydrationEpochRef, programmaticWriteGuard, itemRules, revalidateOnPerRowSync, revalidateTargetsOnPerRowSync]);

  useEffect(() => {
    if (effects.length === 0 || watchFields.length === 0) return;

    const watchMap: Record<string, unknown> = {};
    watchFields.forEach((field, idx) => {
      watchMap[field] = Array.isArray(watchedValues) ? watchedValues[idx] : watchedValues;
    });

    if (isInitialAsyncRef.current) {
      isInitialAsyncRef.current = false;
      prevAsyncValuesRef.current = { ...watchMap };
      return;
    }

    if (hydratingRef?.current) {
      prevAsyncValuesRef.current = { ...watchMap };
      return;
    }

    if (hydrationEpochRef && hydrationEpochRef.current !== prevEpochRef.current) {
      prevAsyncValuesRef.current = { ...watchMap };
      prevEpochRef.current = hydrationEpochRef.current;
      return;
    }

    const actionEffects = effects.filter((e) => e.actionId);
    for (const effect of actionEffects) {
      const watches = Array.isArray(effect.watch) ? effect.watch : [effect.watch];
      const hasChanged = watches.some((w) => watchMap[w] !== prevAsyncValuesRef.current[w]);
      if (!hasChanged) continue;

      if (effect.condition) {
        const currentValues = form.getValues() as Record<string, unknown>;
        const condCtx: ConditionContext = {
          values: currentValues,
          previousValues: prevFormSnapshotRef.current ?? undefined,
          meta,
          getOptionProp,
        };
        if (!evaluateCondition(effect.condition, condCtx)) continue;
      }

      const effectKey = effect.id ?? effect.actionId!;
      const existingTimeout = timeoutsRef.current.get(effectKey);
      if (existingTimeout) clearTimeout(existingTimeout);

      const existingController = abortControllersRef.current.get(effectKey);
      if (existingController) existingController.abort();

      const debounceMs = effect.debounceMs ?? 300;
      const controller = new AbortController();
      abortControllersRef.current.set(effectKey, controller);

      const timeout = setTimeout(() => {
        executeActionId(effect, controller.signal);
      }, debounceMs);
      timeoutsRef.current.set(effectKey, timeout);
    }

    prevAsyncValuesRef.current = { ...watchMap };
  }, [watchedValues, watchFields, effects, form, arrayName, meta, getOptionProp, hydratingRef, hydrationEpochRef, executeActionId]);

  useEffect(() => {
    return () => {
      for (const timeout of timeoutsRef.current.values()) {
        clearTimeout(timeout);
      }
      timeoutsRef.current.clear();
      for (const controller of abortControllersRef.current.values()) {
        controller.abort();
      }
      abortControllersRef.current.clear();
    };
  }, []);
}

/**
 * Replaces `{i}` tokens in Compute field name references with the actual row index.
 * This enables `perRow` computes to reference same-row field values
 * (e.g. `purchasePeriods.{i}.periodStartDate` → `purchasePeriods.0.periodStartDate`).
 */
function replaceRowIndex(compute: Compute, rowPath: string): Compute {
  const raw = JSON.stringify(compute);
  if (!raw.includes('{i}')) return compute;
  const idx = rowPath.split('.').pop()!;
  return JSON.parse(raw.replaceAll('{i}', idx)) as Compute;
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

function scheduleArrayRevalidationAfterPerRowSync(params: {
  form: UseFormReturn<Record<string, unknown>>;
  arrayName: string;
  fieldsToWrite: string[];
  itemRules?: FormRule[];
  revalidateOnPerRowSync?: boolean;
  revalidateTargetsOnPerRowSync?: string[];
}): void {
  if (params.revalidateOnPerRowSync === false) return;

  const writtenRowFields = collectWrittenRowFieldNames(params.arrayName, params.fieldsToWrite);
  const targets =
    params.revalidateTargetsOnPerRowSync ??
    deriveArrayRevalidateTargets(params.itemRules, writtenRowFields);

  const triggerPaths = buildArrayRevalidateTriggerPaths(
    params.arrayName,
    params.fieldsToWrite,
    targets,
  );
  if (triggerPaths.length === 0) return;

  queueMicrotask(() => {
    for (const path of triggerPaths) {
      void params.form.trigger(path);
    }
  });
}
